import * as React from 'react';
import Latex from 'react-latex-next';

// Lazy load KaTeX CSS to reduce initial bundle size
const loadKatexCSS = () => {
  if (
    typeof document !== 'undefined' &&
    !document.querySelector('link[href*="katex"]')
  ) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.css';
    link.crossOrigin = 'anonymous';
    document.head.append(link);
  }
};

// Simple HTML sanitization function to prevent XSS
const sanitizeHtml = (html: string): string => {
  if (typeof document === 'undefined') {
    // Server-side: use simple string replacement for basic safety
    return html
      .replaceAll(/<script[^>]*>.*?<\/script>/gi, '')
      .replaceAll(/<iframe[^>]*>.*?<\/iframe>/gi, '')
      .replaceAll(/<object[^>]*>.*?<\/object>/gi, '')
      .replaceAll(/<embed[^>]*>/gi, '')
      .replaceAll(/<link[^>]*>/gi, '')
      .replaceAll(/<meta[^>]*>/gi, '')
      .replaceAll(/on\w+="[^"]*"/gi, '')
      .replaceAll(/javascript:/gi, '')
      .replaceAll(/data:/gi, '')
      .replaceAll(/vbscript:/gi, '');
  }

  // Client-side: use DOM-based sanitization for better security
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Remove dangerous elements
  const dangerousTags = ['script', 'iframe', 'object', 'embed', 'link', 'meta'];
  for (const tag of dangerousTags) {
    const elements = tempDiv.querySelectorAll(tag);
    for (const el of elements) {
      el.remove();
    }
  }

  // Remove dangerous attributes
  const allElements = tempDiv.querySelectorAll('*');
  for (const el of allElements) {
    const attributes = [...el.attributes];
    for (const attr of attributes) {
      const name = attr.name.toLowerCase();
      const value = attr.value.toLowerCase();
      const isEventHandler = name.startsWith('on');
      const isUrlAttribute = ['href', 'src', 'xlink:href'].includes(name);
      const isAllowedScheme =
        value.startsWith('http:') ||
        value.startsWith('https:') ||
        value.startsWith('/') ||
        value.startsWith('./') ||
        value.startsWith('../');

      if (isEventHandler || (isUrlAttribute && !isAllowedScheme)) {
        el.removeAttribute(attr.name);
      }
    }
  }

  return tempDiv.innerHTML;
};

/**
 * LaTeX Renderer Component using react-latex-next
 *
 * Renders LaTeX expressions using KaTeX for proper mathematical typesetting.
 * Supports all standard LaTeX mathematical notation including arrays, matrices, and complex expressions.
 *
 * Security Features:
 * - HTML sanitization to prevent XSS attacks
 * - Lazy loading of KaTeX CSS to reduce initial bundle size
 * - Safe handling of user-generated content
 *
 * Performance Optimizations:
 * - Memoized content processing to prevent unnecessary re-renders
 * - Dynamic CSS loading to reduce initial bundle size
 * - Efficient regex-based LaTeX detection
 */

interface LaTeXRendererProps {
  content: string;
  className?: string;
}

const LaTeXRenderer: React.FC<LaTeXRendererProps> = ({
  content,
  className = '',
}) => {
  // Load KaTeX CSS on first render
  React.useEffect(() => {
    loadKatexCSS();
  }, []);

  // Split content into LaTeX and non-LaTeX parts
  const renderContent = React.useMemo(() => {
    // Ensure content is a string
    if (typeof content !== 'string') {
      return content;
    }

    // Process content to handle HTML tags
    const processedContent = content
      .replaceAll('</br>', '<br/>')
      .replaceAll('&quot;', '"');

    // Find LaTeX expressions (both $...$ and $$...$$ and \(...\) and \[...\])
    const latexRegex =
      /(\$\$[\s\S]*?\$\$|\$[^$]*?\$|\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\))/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let key = 0;

    let match;
    while ((match = latexRegex.exec(processedContent)) !== null) {
      // Add text before LaTeX
      if (match.index > lastIndex) {
        const textPart = processedContent.slice(lastIndex, match.index);
        parts.push(
          <span
            key={key++}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(textPart) }}
          />
        );
      }

      // Add LaTeX part - pass as plain text string to react-latex-next
      const [latexContent] = match;
      parts.push(
        <Latex
          key={key++}
          delimiters={[
            { left: '$$', right: '$$', display: true },
            { left: '$', right: '$', display: false },
            { left: String.raw`\[`, right: String.raw`\]`, display: true },
            { left: String.raw`\(`, right: String.raw`\)`, display: false },
          ]}
          strict={false}
        >
          {latexContent}
        </Latex>
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < processedContent.length) {
      const remainingText = processedContent.slice(lastIndex);
      parts.push(
        <span
          key={key++}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(remainingText) }}
        />
      );
    }

    return parts.length > 0
      ? parts
      : [
          <span
            key={0}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(processedContent) }}
          />,
        ];
  }, [content]);

  return <div className={className}>{renderContent}</div>;
};

export { LaTeXRenderer };
